// ===================================================
// components/Layout/Sidebar.jsx
// Rail + hover-dock + flyout IA (STYLESYSTEM). Accordion удалён.
// ===================================================
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard as DashboardOutlined,
  Package as ShoppingOutlined,
  Store as ShopOutlined,
  Home as HomeOutlined,
  Truck as TruckOutlined,
  Users as TeamOutlined,
  RefreshCw as SyncOutlined,
  Settings as SettingOutlined,
  Book as BookOutlined,
  Webhook as ApiOutlined,
  DownloadCloud as CloudDownloadOutlined,
  Grid as AppstoreOutlined,
  User as UserOutlined,
  Clock as ClockCircleOutlined,
  Network as ApartmentOutlined,
  QrCode as QrcodeOutlined,
  Calendar as CalendarOutlined,
  PanelLeftClose as MenuFoldOutlined,
  PanelLeftOpen as MenuUnfoldOutlined,
  Folder as FolderOutlined,
  Globe as GlobalOutlined,
  Percent as PercentageOutlined,
  FileText as FileTextOutlined,
  Palette as BgColorsOutlined,
  CalendarClock as ScheduleOutlined,
  UploadCloud as CloudUploadOutlined,
  Database as DatabaseOutlined,
  X as CloseOutlined,
  ShoppingCart as ShoppingCartOutlined,
  BarChart2 as BarChartOutlined,
  Bell as BellOutlined,
  CircleDollarSign as DollarOutlined,
  Image as PictureOutlined,
  Landmark as BankOutlined,
  Sliders as ControlOutlined,
  Mail as MailOutlined,
  Layers as PartitionOutlined,
  Search as SearchOutlined,
  Key as KeyOutlined,
  Zap as ThunderboltOutlined,
  ShieldCheck as SafetyCertificateOutlined,
  ScrollText as LogsOutlined,
  MessageSquare as MessageSquareOutlined,
  Scissors as ScissorOutlined,
  ClipboardList as ComplianceIcon,
  Rss as RssOutlined,
  Server as ServerOutlined,
  Rocket as RocketOutlined,
  Link as LinkOutlined,
  ChevronRight as ChevronRightOutlined,
  SlidersHorizontal as SlidersHorizontalOutlined,
} from 'lucide-react';
import LogoImage from 'components/Common/LogoImage';
import { usePermissions } from 'hooks/usePermissions';
import { PERMISSIONS } from 'utils/constants';
import {
  USERS_PORTAL_URL,
  WALLET_URL,
  BILLING_URL,
  SCHEDULER_DASHBOARD_URL,
  BACKUP_DASHBOARD_URL,
  DELGRAM_URL,
  MARKET_MODULE_URL,
  MARKET_CONNECTION_URL,
  MPLAZA_APP_CONNECTION_URL,
  MPLAZA_SELLER_CONNECTION_URL,
  CORE_MONITORING_DASHBOARD_URL,
} from 'utils/externalLinks';
import { appSidebarLogoSources } from '../../utils/platformAssets';
import WayGptIcon from '../Common/WayGptIcon';
import { isProtectedSystemCompanyId } from '../../utils/systemCompany';
import seoService from '../../services/seo.service';
import { buildExternalModuleEntryUrl } from '../../utils/externalModuleEntry';
import { useAuth } from 'hooks/useAuth';
import { useConcurensAccess } from '../../hooks/useConcurensAccess';
import {
  buildNavigationHubs,
  applyNavigationPreferences,
  applyNavigationBadges,
  matchSelectedKey,
  isHubActive,
  getHubQuickPickItems,
  findHubById,
} from 'config/navigation.registry';
import { resolveActiveModulesNav } from '../../utils/resolveActiveModulesNav';
import { useNavigationPreferences } from 'hooks/useNavigationPreferences';
import CompanySwitcher from './CompanySwitcher';
import NavPresetEditor from './NavPresetEditor';
import NavFlyout from './NavFlyout';
import NavBadge from './NavBadge';
import NavFavoriteStar from './NavFavoriteStar';
import api from '../../services/index';
import {
  MENU_FAVORITES_CHANGED,
  normalizeFavoriteKeys,
  buildHubQuickPicksFromFavorites,
} from '../../utils/menuFavoritesSync';
import { useAppInfo } from '../../hooks/useAppInfo';

export const SIDEBAR_COLLAPSED_EVENT = 'mb_sidebar_collapsed_change';
export const SIDEBAR_MOBILE_OPEN_EVENT = 'mb_sidebar_mobile_open';

const RubleIcon = () => (
  <span className="mb-sidebar-ruble-icon">&#8381;</span>
);

const SIDEBAR_ICONS = {
  appstore: AppstoreOutlined,
  dashboard: DashboardOutlined,
  robot: WayGptIcon,
  messageSquare: MessageSquareOutlined,
  shopping: ShoppingOutlined,
  apartment: ApartmentOutlined,
  partition: PartitionOutlined,
  book: BookOutlined,
  percentage: PercentageOutlined,
  picture: PictureOutlined,
  truck: TruckOutlined,
  clock: ClockCircleOutlined,
  cloudDownload: CloudDownloadOutlined,
  home: HomeOutlined,
  calendar: CalendarOutlined,
  qrcode: QrcodeOutlined,
  cart: ShoppingCartOutlined,
  shop: ShopOutlined,
  barChart: BarChartOutlined,
  settings: SettingOutlined,
  fileText: FileTextOutlined,
  scissor: ScissorOutlined,
  safetyCertificate: SafetyCertificateOutlined,
  api: ApiOutlined,
  sync: SyncOutlined,
  team: TeamOutlined,
  bell: BellOutlined,
  key: KeyOutlined,
  ruble: RubleIcon,
  dollar: DollarOutlined,
  bank: BankOutlined,
  compliance: ComplianceIcon,
  thunder: ThunderboltOutlined,
  user: UserOutlined,
  folder: FolderOutlined,
  search: SearchOutlined,
  mail: MailOutlined,
  control: ControlOutlined,
  schedule: ScheduleOutlined,
  global: GlobalOutlined,
  bgColors: BgColorsOutlined,
  database: DatabaseOutlined,
  cloudUpload: CloudUploadOutlined,
  server: ServerOutlined,
  logs: LogsOutlined,
  rss: RssOutlined,
  link: LinkOutlined,
  rocket: RocketOutlined,
};

const Sidebar = () => {
  const location = useLocation();
  const { hasPermission, isSuperAdmin } = usePermissions();
  const { user, company } = useAuth();
  const { version: appVersion } = useAppInfo();
  const isCompanyInactive = company?.is_active === false;
  const hasCompany = !!company?.id;
  const verificationStatus = company?.verification_status || company?.verificationStatus || null;
  const isVerifiedCompany =
    company?.verified === true
    || verificationStatus === 'verified'
    || Boolean(company?.verified_at);
  const superAdminUser = Boolean(isSuperAdmin());
  const { allowed: concurensAllowed } = useConcurensAccess({
    enabled: Boolean(user?.id),
  });

  const {
    prefs,
    saving,
    setActivePreset,
    setCollapsed: persistCollapsed,
    setHubOrder,
    setHiddenHubIds,
    setDockDisplayMode,
    setHubOverride,
    setHubOverrides,
    resetToDefault,
    savePersonalPreset,
    deletePersonalPreset,
    importSnapshot,
    exportSnapshot,
  } = useNavigationPreferences({ enabled: Boolean(user?.id) });

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof prefs?.sidebarCollapsed === 'boolean' && prefs?.updatedAt) {
      return Boolean(prefs.sidebarCollapsed);
    }
    try {
      const legacy = localStorage.getItem('mb_sidebar_collapsed');
      if (legacy !== null) return legacy === 'true';
    } catch { /* ignore */ }
    return Boolean(prefs?.sidebarCollapsed);
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [flyoutHubId, setFlyoutHubId] = useState(null);
  const [presetEditorOpen, setPresetEditorOpen] = useState(false);
  const [dockHubId, setDockHubId] = useState(null);
  const [dockAnchor, setDockAnchor] = useState(null);
  const [favoriteKeys, setFavoriteKeys] = useState([]);
  const dockHideTimer = useRef(null);
  const railItemRefs = useRef(new Map());
  const favoritesSyncedRef = useRef('');
  const [siteLogoUrl, setSiteLogoUrl] = useState(null);

  // Sync collapsed from server prefs once hydrated (avoid flicker: local mirror already seeded)
  useEffect(() => {
    if (typeof prefs?.sidebarCollapsed === 'boolean') {
      setCollapsed(prefs.sidebarCollapsed);
    }
  }, [prefs?.sidebarCollapsed]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await seoService.getMetaTags('landing');
        const url = res?.data?.branding?.site_logo_url;
        if (!cancelled && url) setSiteLogoUrl(url);
      } catch {
        /* fallback in appSidebarLogoSources */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const logoSources = useMemo(
    () => appSidebarLogoSources(company?.settings?.branding?.logo_url, siteLogoUrl),
    [company?.settings?.branding?.logo_url, siteLogoUrl]
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      setIsMobile(false);
      return undefined;
    }
    /* Align with sidebar.css / Header: hide desktop rail at max-width 768px */
    const mql = window.matchMedia('(max-width: 768px)');
    const apply = (e) => setIsMobile(e.matches);
    apply(mql);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', apply);
      return () => mql.removeEventListener('change', apply);
    }
    mql.addListener(apply);
    return () => mql.removeListener(apply);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setFlyoutHubId(null);
    setDockHubId(null);
  }, [location.pathname]);

  useEffect(() => {
    const handler = () => {
      // Header показывает гамбургер по CSS @media; JS isMobile мог ещё не совпасть —
      // при клике принудительно синхронизируем и открываем панель.
      try {
        if (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
          && window.matchMedia('(max-width: 768px)').matches) {
          setIsMobile(true);
        }
      } catch (_) { /* ignore */ }
      setMobileOpen(true);
    };
    window.addEventListener(SIDEBAR_MOBILE_OPEN_EVENT, handler);
    return () => window.removeEventListener(SIDEBAR_MOBILE_OPEN_EVENT, handler);
  }, []);

  useEffect(() => {
    if (!isMobile || !mobileOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (flyoutHubId) setFlyoutHubId(null);
        else if (presetEditorOpen) setPresetEditorOpen(false);
        else setMobileOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [isMobile, mobileOpen, flyoutHubId, presetEditorOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (flyoutHubId) setFlyoutHubId(null);
      else if (presetEditorOpen) setPresetEditorOpen(false);
      else setDockHubId(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [flyoutHubId, presetEditorOpen]);

  useEffect(() => {
    try {
      localStorage.setItem('mb_sidebar_collapsed', String(collapsed));
    } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent(SIDEBAR_COLLAPSED_EVENT, { detail: { collapsed } }));
  }, [collapsed]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      persistCollapsed(next);
      return next;
    });
  }, [persistCollapsed]);

  const urls = useMemo(() => ({
    USERS_PORTAL_URL,
    WALLET_URL,
    BILLING_URL,
    SCHEDULER_DASHBOARD_URL,
    BACKUP_DASHBOARD_URL,
    DELGRAM_URL,
    MARKET_MODULE_URL,
    MARKET_CONNECTION_URL,
    MPLAZA_APP_CONNECTION_URL,
    MPLAZA_SELLER_CONNECTION_URL,
    CORE_MONITORING_DASHBOARD_URL,
  }), []);

  const activeModules = useMemo(
    () => resolveActiveModulesNav(company, { isSuperAdmin: superAdminUser }),
    [company, superAdminUser]
  );

  const baseHubs = useMemo(
    () => buildNavigationHubs({
      urls,
      permissions: {
        has: hasPermission,
        isSuperAdmin: () => superAdminUser,
        PERMISSIONS,
      },
      flags: {
        hasCompany,
        isCompanyInactive,
        isVerifiedCompany,
        companyId: company?.id || null,
        isSystemMarkbaseCompany: isProtectedSystemCompanyId(company?.id),
        concurensAllowed,
      },
      activeModules,
    }),
    [
      urls,
      hasPermission,
      superAdminUser,
      hasCompany,
      isCompanyInactive,
      isVerifiedCompany,
      company?.id,
      concurensAllowed,
      activeModules,
    ]
  );

  const hubs = useMemo(
    () => applyNavigationBadges(
      applyNavigationPreferences(baseHubs, prefs),
      prefs?.platform
    ),
    [baseHubs, prefs]
  );

  const selectedKey = useMemo(
    () => matchSelectedKey(location.pathname, location.search, hubs),
    [location.pathname, location.search, hubs]
  );

  const flyoutHub = flyoutHubId ? findHubById(hubs, flyoutHubId) || findHubById(baseHubs, flyoutHubId) : null;
  /** Unfiltered registry hub — needed so add-back can see hidden items stripped by prefs. */
  const flyoutBaseHub = flyoutHubId ? findHubById(baseHubs, flyoutHubId) : null;
  const isCollapsedView = collapsed && !isMobile;

  const clearDockTimer = useCallback(() => {
    if (dockHideTimer.current) {
      clearTimeout(dockHideTimer.current);
      dockHideTimer.current = null;
    }
  }, []);

  const showDock = useCallback((hubId) => {
    clearDockTimer();
    setDockHubId(hubId);
    const el = railItemRefs.current.get(hubId);
    if (el) {
      const rect = el.getBoundingClientRect();
      setDockAnchor({
        top: rect.top,
        left: rect.right + 6,
        hubHeight: rect.height,
      });
    }
  }, [clearDockTimer]);

  const hideDockSoon = useCallback((ms = 220) => {
    clearDockTimer();
    dockHideTimer.current = setTimeout(() => {
      setDockHubId(null);
      setDockAnchor(null);
    }, ms);
  }, [clearDockTimer]);

  useEffect(() => () => clearDockTimer(), [clearDockTimer]);

  // SSOT: user_menu_favorites → dock + hub quickPicks (cross-device)
  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const data = await api.users.getMenuFavorites();
        if (cancelled) return;
        const keys = normalizeFavoriteKeys(data);
        setFavoriteKeys(keys);
      } catch {
        /* silent */
      }
    };
    load();
    const onFav = () => { load(); };
    window.addEventListener(MENU_FAVORITES_CHANGED, onFav);
    return () => {
      cancelled = true;
      window.removeEventListener(MENU_FAVORITES_CHANGED, onFav);
    };
  }, [user?.id]);

  // Mirror favorites into hubOverrides.quickPicks so stars + dock stay aligned
  useEffect(() => {
    if (!user?.id || !baseHubs?.length) return;
    const sig = `${favoriteKeys.join('|')}::${baseHubs.map((h) => h.id).join(',')}`;
    if (favoritesSyncedRef.current === sig) return;
    favoritesSyncedRef.current = sig;
    const next = buildHubQuickPicksFromFavorites(
      baseHubs,
      favoriteKeys,
      prefs?.hubOverrides || {}
    );
    const prevJson = JSON.stringify(prefs?.hubOverrides || {});
    const nextJson = JSON.stringify(next);
    if (prevJson !== nextJson) {
      setHubOverrides(next);
    }
  }, [user?.id, favoriteKeys, baseHubs, prefs?.hubOverrides, setHubOverrides]);

  const openFlyout = useCallback((hubId) => {
    clearDockTimer();
    setDockHubId(null);
    setDockAnchor(null);
    setFlyoutHubId(hubId);
  }, [clearDockTimer]);

  const openPresetEditor = useCallback(() => {
    setPresetEditorOpen(true);
  }, []);

  const resolveItemHref = useCallback((it) => {
    if (!it?.href) return '#';
    if (it.external) {
      return buildExternalModuleEntryUrl(it.href) || it.href;
    }
    return it.href;
  }, []);

  const avatarUrl = user?.avatar_url || user?.avatarUrl || null;

  const renderHubButton = (hub) => {
    const Icon = SIDEBAR_ICONS[hub.iconKey] || AppstoreOutlined;
    const hubActive = isHubActive(hub, selectedKey);
    const dockOpen = dockHubId === hub.id;
    const btn = (
      <button
        type="button"
        ref={(el) => {
          if (el) railItemRefs.current.set(hub.id, el);
          else railItemRefs.current.delete(hub.id);
        }}
        className={`mb-rail-item ${hubActive ? 'mb-rail-item--hub-active' : ''} ${dockOpen ? 'is-dock-open' : ''}`}
        data-hub={hub.id}
        aria-expanded={flyoutHubId === hub.id || dockOpen}
        aria-label={hub.label}
        title={isCollapsedView ? hub.label : undefined}
        onClick={() => openFlyout(hub.id)}
        onMouseEnter={() => !isMobile && showDock(hub.id)}
        onMouseLeave={() => !isMobile && hideDockSoon()}
        onFocus={() => !isMobile && showDock(hub.id)}
        onBlur={() => !isMobile && hideDockSoon()}
      >
        <span className="mb-rail-item__icon"><Icon size={isMobile ? 16 : 20} /></span>
        {!isCollapsedView && <span className="mb-rail-item__label">{hub.label}</span>}
      </button>
    );

    return (
      <div key={hub.id} className="mb-rail-item-wrap">
        {btn}
      </div>
    );
  };

  const dockHub = dockHubId ? findHubById(hubs, dockHubId) : null;
  const dockQuick = dockHub
    ? getHubQuickPickItems(dockHub, {
      mode: prefs?.dockDisplayMode || 'always_first_3',
      globalFavoriteKeys: favoriteKeys,
    })
    : [];
  const dockVisible = Boolean(dockHub && dockAnchor && !isMobile && prefs?.dockDisplayMode !== 'hidden');

  const floatingDock = dockVisible && typeof document !== 'undefined'
    ? createPortal(
      <div
        className="mb-rail-hover-dock mb-rail-hover-dock--flyout is-open"
        style={{
          top: Math.max(8, dockAnchor.top),
          left: dockAnchor.left,
        }}
        onMouseEnter={clearDockTimer}
        onMouseLeave={() => hideDockSoon()}
        role="menu"
        aria-label={dockHub.label}
      >
        <div className="mb-rail-hover-dock__inner">
          <div className="mb-rail-hover-dock__list">
            {dockQuick.length === 0 && (
              <p className="mb-rail-hover-dock__empty">Нет избранных · откройте все</p>
            )}
            {dockQuick.map((it) => {
              const QIcon = SIDEBAR_ICONS[it.iconKey] || AppstoreOutlined;
              const active = selectedKey === it.key || selectedKey === it.href;
              const href = resolveItemHref(it);
              const cls = `mb-rail-dock-link ${active ? 'mb-rail-dock-link--active' : ''}`;
              const label = (
                <>
                  <span className="mb-rail-dock-link__icon"><QIcon size={15} /></span>
                  <span className="mb-rail-dock-link__label">{it.label}</span>
                  <NavBadge type={it.badge} />
                  <NavFavoriteStar
                    active={Boolean(it.isFavorite) || favoriteKeys.includes(it.key) || favoriteKeys.includes(it.href)}
                  />
                </>
              );
              if (it.external) {
                return (
                  <a key={it.key} href={href} className={cls} target="_blank" rel="noopener noreferrer">
                    {label}
                  </a>
                );
              }
              return (
                <Link key={it.key} to={href} className={cls}>
                  {label}
                </Link>
              );
            })}
          </div>
          <button
            type="button"
            className="mb-rail-hover-dock__all"
            onClick={() => openFlyout(dockHub.id)}
            title="Открыть полное меню раздела"
            aria-label={`Все подразделы: ${dockHub.label}`}
          >
            <span className="mb-rail-hover-dock__all-label">Все подразделы</span>
            <ChevronRightOutlined size={16} aria-hidden />
          </button>
        </div>
      </div>,
      document.body
    )
    : null;

  const renderSidebarContent = () => (
    <div className="mb-sidebar-inner">
      <div className="mb-sidebar-header mb-sidebar-header--logo-row">
        <div className="mb-sidebar-logo">
          <div
            className={
              isCollapsedView
                ? 'mb-sidebar-logo-container mb-sidebar-logo-container--compact'
                : 'mb-sidebar-logo-container'
            }
          >
            <LogoImage
              sources={logoSources}
              alt="Маркбэйс"
              mode="image"
              height={isCollapsedView ? 28 : 32}
              maxWidth={isCollapsedView ? 36 : 120}
              style={{ marginRight: 0 }}
            />
          </div>
          {!isCollapsedView && (
            <>
              <span className="mb-sidebar-brand">Маркбэйс</span>
              <span className="mb-sidebar-brand-version" title={`Версия ${appVersion}`}>
                v{appVersion}
              </span>
            </>
          )}
        </div>
      </div>

      <div className="mb-sidebar-configure-row">
        {isCollapsedView ? (
          <button
            type="button"
            className="mb-sidebar-configure-btn mb-sidebar-configure-btn--icon"
            onClick={openPresetEditor}
            aria-label="Настроить меню"
            title="Настроить меню"
          >
            <SlidersHorizontalOutlined size={18} />
          </button>
        ) : (
          <button
            type="button"
            className="mb-sidebar-configure-btn"
            onClick={openPresetEditor}
            aria-label="Настроить меню"
          >
            <SlidersHorizontalOutlined size={16} />
            <span>Настроить</span>
          </button>
        )}
      </div>

      {isMobile && (
        <div className="mb-sidebar-mobile-company">
          <CompanySwitcher placement="bottomLeft" />
        </div>
      )}

      <nav className="mb-sidebar-nav mb-rail-nav" aria-label="Основная навигация">
        {hubs.map(renderHubButton)}
      </nav>

      <div className="mb-sidebar-footer">
        {isMobile && (
          <Link
            to="/commerce-hub"
            className="mb-nav-item-wrapper mb-nav-link-reset"
            onClick={() => setMobileOpen(false)}
          >
            <div className="mb-nav-item">
              <span className="mb-nav-item__icon"><RocketOutlined size={18} /></span>
              <span className="mb-nav-item__label">Мастер настройки</span>
            </div>
          </Link>
        )}

        <Link
          to="/settings"
          className="mb-nav-item-wrapper mb-nav-link-reset"
          onClick={() => { if (isMobile) setMobileOpen(false); }}
          title="Настройки"
          aria-label="Настройки"
        >
          <div className={`mb-nav-item ${selectedKey === '/settings' ? 'mb-nav-item--active' : ''}`}>
            <span className="mb-nav-item__icon"><SettingOutlined size={18} /></span>
            {!isCollapsedView && <span className="mb-nav-item__label">Настройки</span>}
          </div>
        </Link>

        <Link
          to="/settings/profile"
          className={`mb-sidebar-profile mb-nav-link-reset ${selectedKey === '/settings/profile' ? 'mb-sidebar-profile--active' : ''}`}
          onClick={() => { if (isMobile) setMobileOpen(false); }}
        >
          <div className="mb-sidebar-profile__avatar">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" />
            ) : (
              <UserOutlined size={24} color="var(--text-3)" />
            )}
          </div>
          {!isCollapsedView && (
            <div className="mb-sidebar-profile__info">
              <span className="mb-sidebar-profile__name">{user?.display_name || user?.name || user?.email || 'Пользователь'}</span>
              <span className="mb-sidebar-profile__email">{user?.email || ''}</span>
            </div>
          )}
        </Link>

        {!isMobile && (
          <button
            type="button"
            className="mb-sidebar-collapse-btn"
            onClick={toggleCollapsed}
            title={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
          >
            {isCollapsedView ? <MenuUnfoldOutlined size={20} /> : <MenuFoldOutlined size={20} />}
            {!isCollapsedView && <span className="mb-sidebar-collapse-btn__label">Свернуть</span>}
          </button>
        )}
      </div>
    </div>
  );

  const editors = (
    <>
      <NavFlyout
        open={Boolean(flyoutHub)}
        hub={flyoutHub}
        baseHub={flyoutBaseHub}
        allBaseHubs={baseHubs}
        selectedKey={selectedKey}
        icons={SIDEBAR_ICONS}
        favoriteKeys={favoriteKeys}
        onClose={() => setFlyoutHubId(null)}
        onNavigate={() => {
          setFlyoutHubId(null);
          if (isMobile) setMobileOpen(false);
        }}
        hubOverride={flyoutHub ? prefs?.hubOverrides?.[flyoutHub.id] : null}
        onSaveHubOverride={(ov) => flyoutHub && setHubOverride(flyoutHub.id, ov)}
        onResetHub={(hubId) => setHubOverride(hubId, null)}
      />
      <NavPresetEditor
        open={presetEditorOpen}
        onClose={() => setPresetEditorOpen(false)}
        hubs={baseHubs}
        prefs={prefs}
        onApplyPreset={setActivePreset}
        onSetHubOrder={setHubOrder}
        onSetHiddenHubIds={setHiddenHubIds}
        onSetDockDisplayMode={setDockDisplayMode}
        onReset={resetToDefault}
        onSavePersonal={(name) => savePersonalPreset({ name })}
        onDeletePersonal={(id) => deletePersonalPreset(id)}
        onExport={exportSnapshot}
        onImport={importSnapshot}
        saving={saving}
      />
      {floatingDock}
    </>
  );

  const mobileDrawer = (isMobile || mobileOpen) && typeof document !== 'undefined'
    ? createPortal(
      <>
        {mobileOpen ? (
          <button
            type="button"
            className="mb-sidebar-mobile-backdrop"
            aria-label="Закрыть меню"
            onClick={() => setMobileOpen(false)}
          />
        ) : null}
        <div
          className={`mb-sidebar-mobile-panel${mobileOpen ? ' mb-sidebar-mobile-panel--open' : ''}`}
          role="dialog"
          aria-modal={mobileOpen}
          aria-hidden={!mobileOpen}
          aria-label="Меню навигации"
          {...(!mobileOpen ? { inert: true } : {})}
        >
          <div className="mb-sidebar-mobile-panel__inner">
            <div className="mb-sidebar-drawer-close">
              <button type="button" onClick={() => setMobileOpen(false)} className="mb-sidebar-toggle">
                <CloseOutlined size={24} aria-hidden />
              </button>
            </div>
            {renderSidebarContent()}
          </div>
        </div>
      </>,
      document.body
    )
    : null;

  if (isMobile) {
    return (
      <>
        {mobileDrawer}
        {editors}
      </>
    );
  }

  return (
    <>
      <aside className={`mb-sidebar ${collapsed ? 'mb-sidebar--collapsed' : 'mb-sidebar--expanded'}`}>
        {renderSidebarContent()}
      </aside>
      {/* На узком viewport гамбургер CSS-видимый: если isMobile ещё false — панель всё равно в portal */}
      {mobileDrawer}
      {editors}
    </>
  );
};

export default Sidebar;
